import { useState } from 'react';
import {
  Field,
  FormRenderProps,
  FormMeta,
  ValidateTrigger,
} from '@flowgram.ai/free-layout-editor';
import { Select } from '@douyinfe/semi-ui';

import { setFieldValue, getFieldValue } from '../../utils/common';
import { FlowNodeJSON } from '../../typings';
import { useIsSidebar } from '../../hooks';
import { CodeEditor } from '../../form-components/form-inputs';
import { FormHeader, FormContent, FormOutputs, FormItem } from '../../form-components';
import { DataSourceType, NodeFieldsEnum } from '../constants';

// CodeMirror styles are now handled by @uiw/react-codemirror
import './index.css';

const dataSourceOptions = [
  { label: '酒旅公共库', value: DataSourceType.BELINK },
  { label: '电商公共库', value: DataSourceType.ECOMMERCE },
  { label: '电商用户库', value: DataSourceType.ECOMMERCE_USER },
  { label: '电商交易库', value: DataSourceType.ECOMMERCE_TRADE },
  { label: '电商营销库', value: DataSourceType.ECOMMERCE_MARKETING },
  { label: '电商任务中心库', value: DataSourceType.ECOMMERCE_TASKCENTER },
  { label: 'BI分析库', value: DataSourceType.BI_ANALYSIS },
  { label: 'AI数据源', value: DataSourceType.AI },
];

export const renderForm = ({ form }: FormRenderProps<FlowNodeJSON>) => {
  const isSidebar = useIsSidebar();

  // 侧边栏输入状态管理
  const [sidebarDataSource, setSidebarDataSource] = useState(
    getFieldValue(NodeFieldsEnum.dataSource, form) || DataSourceType.BELINK
  );
  const [sidebarSql, setSidebarSql] = useState(getFieldValue(NodeFieldsEnum.sql, form) || '');

  if (isSidebar) {
    return (
      <>
        <FormHeader />
        <FormContent>
          <Field
            name="dataSource"
            render={() => (
              <FormItem name="数据源" type="string">
                <Select
                  value={sidebarDataSource}
                  style={{ width: '100%' }}
                  optionList={dataSourceOptions}
                  onChange={(value) => {
                    const val = value as string;
                    setSidebarDataSource(val);
                    setFieldValue(NodeFieldsEnum.dataSource, val, form);
                  }}
                />
              </FormItem>
            )}
          />
          <Field
            name="sql"
            render={() => (
              <FormItem name="SQL 查询" type="string">
                <CodeEditor
                  value={sidebarSql}
                  mode="sql"
                  placeholder="请输入 SQL 查询语句..."
                  enableFormat={true}
                  autoHeight={true}
                  onChange={(value) => {
                    setSidebarSql(value);
                    setFieldValue(NodeFieldsEnum.sql, value, form);
                  }}
                />
              </FormItem>
            )}
          />
        </FormContent>
      </>
    );
  }

  return (
    <>
      <FormHeader />
      <FormContent>
        <FormOutputs />
        <Field
          name="fields"
          render={() => {
            // 直接在 render 函数中获取最新值，确保每次渲染都能读取到 form 的最新数据
            const dataSource = getFieldValue(NodeFieldsEnum.dataSource, form) || DataSourceType.BELINK;
            const sql = getFieldValue(NodeFieldsEnum.sql, form) || '';
            return (
              <>
                <FormItem name="数据源" type="string">
                  {dataSourceOptions.find((opt) => opt.value === dataSource)?.label || dataSource}
                </FormItem>
                <FormItem name="SQL" type="string">
                  {sql}
                </FormItem>
              </>
            );
          }}
        />
      </FormContent>
    </>
  );
};

export const formMeta: FormMeta<FlowNodeJSON> = {
  render: renderForm,
  validateTrigger: ValidateTrigger.onChange,
  validate: {
    title: ({ value }: { value: string }) => (value ? undefined : 'Title is required'),
    fields: ({ value }) => {
      const sqlField = value?.find((f: any) => f.key === NodeFieldsEnum.sql);
      if (!sqlField || !sqlField.value) return 'SQL is required';
      return undefined;
    },
  },
};
